/**
 * What the More sheet lists, as pure data: in a browser tab the stars beyond
 * the bar's four slots, then every catalog group in order; inside an installed
 * app that app's `APP_NAV` members by group and nothing pinned — the rail IS
 * the membership, so there is nothing curated to pin. Never the All areas
 * card (the sheet *is* All areas).
 */
import { APP_NAV } from '@shared/app-identity/appNav';
import { DESTINATION_GROUPS, DESTINATIONS } from '@shared/navigation/destinations';

import { bottomBarSlots } from './starSlots';

import type { AppSlug } from '@shared/app-identity/installedApp';
import type { Destination } from '@shared/navigation/destinations';

export interface MoreSheetGroup {
  label: string;
  items: Destination[];
}

export interface MoreSheetSections {
  /** Stars beyond the first four, in rail order. Empty with four or fewer, and always empty inside an app. */
  pinned: Destination[];
  /** Catalog groups in order, each holding its listable destinations; empty groups dropped. */
  groups: MoreSheetGroup[];
}

/** Every catalog destination the sheet may list: the app's members, or everything but All areas. */
function listable(installedApp: AppSlug | null): Destination[] {
  if (installedApp === null) return DESTINATIONS.filter(d => d.id !== 'areas');
  const members = new Set(APP_NAV[installedApp]);
  return DESTINATIONS.filter(d => members.has(d.id));
}

/** The sheet's contents for this rail and app. */
export function moreSheetSections(
  railIds: readonly string[],
  installedApp: AppSlug | null
): MoreSheetSections {
  const { rest } = bottomBarSlots(railIds);
  const all = listable(installedApp);
  const groups = DESTINATION_GROUPS.map(g => ({
    label: g.label,
    items: all.filter(d => d.group === g.id),
  })).filter(g => g.items.length > 0);
  return { pinned: installedApp === null ? rest : [], groups };
}

/**
 * Does the bar need a More slot? True when the listable catalog holds anything
 * the four slots do not already show — always in a browser tab, in the Inbox
 * app (six members), never in an app whose members all have slots.
 */
export function hasMoreThanSlots(
  railIds: readonly string[],
  installedApp: AppSlug | null
): boolean {
  const slotIds = new Set(bottomBarSlots(railIds).slots.map(d => d.id));
  return listable(installedApp).some(d => !slotIds.has(d.id));
}
